import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminDailyPromptCreateAction,
  adminDailyPromptGetAction,
  adminDailyPromptUpdateAction,
  adminHackWeekSelectOptionsAction
} from "../Action/action";
import config from "../coreFIles/config";
import CKEditorWithSource from "./CKEditorWithSource";
import axios from "axios";
import { getAdminAuthToken } from "../coreFIles/auth";

const DAYS_1_7 = [1, 2, 3, 4, 5, 6, 7];

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);

    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });

    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");

    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function DailyPromptForm({ mode }) {
  const { promptId } = useParams();
  const navigate = useNavigate();

  const [loadingRow, setLoadingRow] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [weekId, setWeekId] = useState("");
  const [day, setDay] = useState("");
  const [title, setTitle] = useState("");
  const [displayOrder, setDisplayOrder] = useState("0");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");

  const [weekOptions, setWeekOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminHackWeekSelectOptionsAction();
        if (cancelled || !res?.success) return;
        setWeekOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setWeekOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (mode !== "edit") return;
      setLoadingRow(true);
      setError("");
      try {
        const res = await adminDailyPromptGetAction(promptId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load daily prompt.");
          return;
        }
        const r = res.data || {};
        setWeekId(r.week_id != null && r.week_id !== "" ? String(r.week_id) : "");
        const dn = parseInt(String(r.day ?? "").replace(/\D/g, ""), 10);
        setDay(Number.isFinite(dn) && dn >= 1 && dn <= 7 ? String(dn) : "");
        setTitle(r.title || "");
        const ord = r.display_order;
        setDisplayOrder(
          ord != null && ord !== "" && Number.isFinite(Number(ord)) ? String(Math.max(0, parseInt(String(ord), 10))) : "0"
        );
        setShortDescription(r.short_description || "");
        setDescription(r.description || "");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load daily prompt.");
      } finally {
        if (!cancelled) setLoadingRow(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [promptId, mode]);

  const validate = () => {
    const errs = {};
    if (!String(weekId || "").trim()) errs.weekId = "Course week is required.";
    if (!String(day || "").trim()) errs.day = "Day is required.";
    else {
      const n = parseInt(String(day), 10);
      if (!Number.isFinite(n) || n < 1 || n > 7) errs.day = "Day must be from 1 to 7.";
    }
    if (!String(title || "").trim()) errs.title = "Title is required.";
    if (!editorPlainText(shortDescription)) errs.shortDescription = "Short description is required.";
    if (!editorPlainText(description)) errs.description = "Description is required.";
    const ordRaw = String(displayOrder ?? "").replace(/\D/g, "");
    const ord = ordRaw === "" ? 0 : parseInt(ordRaw, 10);
    if (!Number.isFinite(ord) || ord < 0) errs.displayOrder = "Display order must be zero or a positive number.";
    return errs;
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the required fields.", { toastId: "daily-prompt-save" });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const ordStr = String(displayOrder ?? "").replace(/\D/g, "");
      const display_order = ordStr === "" ? 0 : Math.max(0, parseInt(ordStr, 10));
      const body = {
        week_id: parseInt(String(weekId), 10),
        day: parseInt(String(day), 10),
        display_order,
        title: String(title).trim(),
        short_description: shortDescription || "",
        description: description || ""
      };
      const res =
        mode === "edit"
          ? await adminDailyPromptUpdateAction(promptId, body)
          : await adminDailyPromptCreateAction(body);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Save failed");

      toast.success(mode === "edit" ? "Daily prompt updated" : "Daily prompt created", {
        toastId: "daily-prompt-save"
      });
      navigate("/daily-prompts");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "daily-prompt-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {mode === "edit" && loadingRow ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save daily prompt.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/daily-prompts">Daily Prompts</Link>
          <span>›</span>
          <span>{mode === "edit" ? "Edit prompt" : "Add prompt"}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/daily-prompts" className="btn btn-outline btn-sm">
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="card mb-6">
            <h2 className="section-title">Prompt details</h2>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Course week <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={weekId}
                  onChange={(e) => {
                    setWeekId(e.target.value);
                    setDay("");
                    setFieldErrors((p) => ({ ...p, weekId: "", day: "" }));
                  }}
                  aria-invalid={!!fieldErrors.weekId}
                  style={fieldErrors.weekId ? { borderColor: "var(--danger)" } : undefined}
                >
                  <option value="">Select course week…</option>
                  {weekOptions.map((w) => (
                    <option key={w.id} value={String(w.id)}>
                      {(w.course_title || `Course ${w.course_id ?? ""}`).trim()} — {w.title || `Week ${w.id}`}
                    </option>
                  ))}
                </select>
                {fieldErrors.weekId ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {fieldErrors.weekId}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>
                  Day (1–7) <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={day}
                  disabled={!String(weekId || "").trim()}
                  onChange={(e) => {
                    setDay(e.target.value);
                    setFieldErrors((p) => ({ ...p, day: "" }));
                  }}
                  aria-invalid={!!fieldErrors.day}
                  style={fieldErrors.day ? { borderColor: "var(--danger)" } : undefined}
                >
                  <option value="">Select day…</option>
                  {DAYS_1_7.map((d) => (
                    <option key={d} value={String(d)}>
                      {d}
                    </option>
                  ))}
                </select>
                {fieldErrors.day ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {fieldErrors.day}
                  </div>
                ) : '' }
              </div>
            </div>

            <div className="form-group">
              <label>Display order</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="0"
                value={displayOrder}
                onInput={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setDisplayOrder(v);
                  setFieldErrors((p) => ({ ...p, displayOrder: "" }));
                }}
                aria-invalid={!!fieldErrors.displayOrder}
                style={fieldErrors.displayOrder ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.displayOrder ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.displayOrder}
                </div>
              ) : '' }
            </div>

            <div className="form-group">
              <label>
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Prompt title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...p, title: "" }));
                }}
                aria-invalid={!!fieldErrors.title}
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.title}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                Short description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                containerStyle={
                  fieldErrors.shortDescription
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={shortDescription}
                onChange={(html) => {
                  setShortDescription(html);
                  setFieldErrors((p) => ({ ...p, shortDescription: "" }));
                }}
              />
              {fieldErrors.shortDescription ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.shortDescription}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                containerStyle={
                  fieldErrors.description
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={description}
                onChange={(html) => {
                  setDescription(html);
                  setFieldErrors((p) => ({ ...p, description: "" }));
                }}
              />
              {fieldErrors.description ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.description}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div>
          <div className="card">
            <h2 className="section-title">Summary</h2>
            <div className="summary-list">
              <div className="flex justify-between">
                <span>Week:</span>{" "}
                <span style={{ textAlign: "right", maxWidth: "65%" }}>
                  {(() => {
                    const wk = weekOptions.find((w) => String(w.id) === String(weekId));
                    if (!wk) return "—";
                    return `${(wk.course_title || `Course ${wk.course_id ?? ""}`).trim()} — ${wk.title || `Week ${wk.id}`}`;
                  })()}
                </span>
              </div>
              <div className="flex justify-between mt-4">
                <span>Day:</span> <span>{day || "—"}</span>
              </div>
              <div className="flex justify-between mt-4">
                <span>Display order:</span> <span>{displayOrder === "" ? "0" : displayOrder || "0"}</span>
              </div>
              <div className="flex justify-between mt-4">
                <span>Title:</span> <span>{title || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => onSave()}
          disabled={saving || loadingRow}
        >
          {saving ? "Saving…" : "Save prompt"}
        </button>
        <Link to="/daily-prompts" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}
